import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkActionToolbar = ({ selectedItems = [], onClearSelection, onBulkAction }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null);

  const bulkActions = [
    {
      id: 'assign',
      label: 'Schichten zuweisen',
      icon: 'UserPlus',
      description: 'Mitarbeiter zu ausgewählten Schichten zuweisen',
      color: 'text-primary',
      requiresSelection: true
    },
    {
      id: 'reassign',
      label: 'Neu zuweisen',
      icon: 'RefreshCw',
      description: 'Bestehende Zuweisungen ändern',
      color: 'text-accent',
      requiresSelection: true
    },
    {
      id: 'duplicate',
      label: 'Schichten duplizieren',
      icon: 'Copy',
      description: 'Ausgewählte Schichten für andere Tage kopieren',
      color: 'text-secondary',
      requiresSelection: true
    },
    {
      id: 'delete',
      label: 'Schichten löschen',
      icon: 'Trash2',
      description: 'Ausgewählte Schichten permanent entfernen',
      color: 'text-error',
      requiresSelection: true,
      dangerous: true
    },
    {
      id: 'template',
      label: 'Als Vorlage speichern',
      icon: 'Save',
      description: 'Schichtmuster als wiederverwendbare Vorlage speichern',
      color: 'text-success',
      requiresSelection: true
    },
    {
      id: 'export',
      label: 'Exportieren',
      icon: 'Download',
      description: 'Schichtdaten in verschiedene Formate exportieren',
      color: 'text-muted-foreground',
      requiresSelection: false
    },
    {
      id: 'optimize',
      label: 'Automatisch optimieren',
      icon: 'Zap',
      description: 'KI-basierte Schichtoptimierung anwenden',
      color: 'text-warning',
      requiresSelection: false
    },
    {
      id: 'notify',
      label: 'Benachrichtigungen senden',
      icon: 'Bell',
      description: 'Mitarbeiter über Schichtänderungen informieren',
      color: 'text-primary',
      requiresSelection: true
    }
  ];

  const quickFilters = [
    { id: 'today', label: 'Heute', icon: 'Calendar' },
    { id: 'week', label: 'Diese Woche', icon: 'CalendarDays' },
    { id: 'vacant', label: 'Offene Schichten', icon: 'AlertCircle' },
    { id: 'conflicts', label: 'Konflikte', icon: 'AlertTriangle' },
    { id: 'overtime', label: 'Überstunden', icon: 'Clock' }
  ];

  const handleBulkAction = (action) => {
    if (action?.requiresSelection && selectedItems?.length === 0) {
      return;
    }
    
    setActiveAction(action?.id);
    onBulkAction?.(action?.id, selectedItems);
    setIsDropdownOpen(false);
    
    // Reset active action after animation
    setTimeout(() => setActiveAction(null), 1000);
  };

  const getActionButton = (action) => {
    const isDisabled = action?.requiresSelection && selectedItems?.length === 0;
    const isActive = activeAction === action?.id;
    
    return (
      <Button
        key={action?.id}
        variant="ghost"
        onClick={() => handleBulkAction(action)}
        disabled={isDisabled}
        className={`w-full justify-start space-x-2 ${action?.dangerous ? 'hover:bg-error/10 hover:text-error' : ''} ${isActive ? 'bg-primary/10 text-primary' : ''}`}
      >
        <Icon name={action?.icon} size={16} className={isDisabled ? 'text-muted-foreground' : action?.color} />
        <div className="text-left">
          <div className="font-medium">{action?.label}</div>
          <div className="text-xs text-muted-foreground">{action?.description}</div>
        </div>
      </Button>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icon name="Settings" size={20} className="text-primary" />
            <h3 className="font-semibold text-foreground">Massenaktionen</h3>
          </div>
          
          {selectedItems?.length > 0 && (
            <div className="flex items-center space-x-2">
              <div className="px-3 py-1 bg-primary/10 text-primary rounded-md text-sm font-medium">
                {selectedItems?.length} ausgewählt
              </div>
              <Button variant="ghost" size="xs" onClick={onClearSelection}>
                <Icon name="X" size={14} />
                Auswahl aufheben
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Quick Filters */}
          <div className="flex items-center space-x-1">
            {quickFilters?.map((filter) => (
              <Button
                key={filter?.id}
                variant="ghost"
                size="xs"
                iconName={filter?.icon}
                iconSize={14}
                className="text-xs"
              >
                {filter?.label}
              </Button>
            ))}
          </div>

          <div className="w-px h-6 bg-border mx-2" />

          {/* Bulk Actions Dropdown */}
          <div className="relative">
            <Button
              variant="default"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              iconName="ChevronDown"
              iconPosition="right"
              iconSize={16}
            >
              Aktionen
            </Button>

            {isDropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-lg shadow-modal z-50">
                <div className="p-2 border-b border-border">
                  <h4 className="font-medium text-popover-foreground mb-1">Verfügbare Aktionen</h4>
                  <p className="text-xs text-muted-foreground">
                    {selectedItems?.length > 0 
                      ? `${selectedItems?.length} Elemente ausgewählt`
                      : 'Wählen Sie Elemente aus, um weitere Optionen zu sehen'
                    }
                  </p>
                </div>
                <div className="p-2 space-y-1 max-h-64 overflow-y-auto">
                  {bulkActions?.map(getActionButton)}
                </div>
              </div>
            )}
          </div>

          {/* Keyboard Shortcuts Info */}
          <Button
            variant="ghost"
            size="icon"
            className="relative group"
          >
            <Icon name="Keyboard" size={16} />
            <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-lg shadow-modal p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <h5 className="font-medium text-popover-foreground mb-2">Tastenkürzel</h5>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Alle auswählen</span>
                  <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Strg+A</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Auswahl aufheben</span>
                  <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Esc</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Löschen</span>
                  <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Entf</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Duplizieren</span>
                  <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Strg+D</kbd>
                </div>
              </div>
            </div>
          </Button>
        </div>
      </div>
      {/* Action Status Bar */}
      {activeAction && (
        <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Loader2" size={16} className="text-primary animate-spin" />
            <span className="text-sm font-medium text-primary">
              Aktion wird ausgeführt...
            </span>
          </div>
        </div>
      )}
      {/* Quick Stats */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-5 gap-4 text-center text-xs">
          <div>
            <div className="text-lg font-semibold text-foreground">24</div>
            <div className="text-muted-foreground">Gesamt Schichten</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-success">18</div>
            <div className="text-muted-foreground">Besetzt</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-warning">4</div>
            <div className="text-muted-foreground">Ausstehend</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-error">2</div>
            <div className="text-muted-foreground">Offen</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-accent">3</div>
            <div className="text-muted-foreground">Konflikte</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActionToolbar;